/**
 * ticket-wallet.design — `@momo/ticket-template/wallet`: how a wallet pass
 * reads, decided once for every process that draws one. The signing composer in
 * the functions bundle and the admin's pass schematic both import this subpath,
 * so the decisions it carries — the pass's colour and how its one panel is
 * produced — are the same fact on the pass a buyer holds and on the preview an
 * operator approves it from. A decision reachable only through one of them is a
 * drift with nothing to catch it: both surfaces render, neither errors, and the
 * one that lies is the one nobody signs.
 *
 * THE COLOUR, AND THE TWO WAYS A VARIANT DECIDES IT. Every variant declares
 * its own ground, ink polarity and panel treatment on one record below, and a
 * new variant refuses to compile until it declares all three.
 *
 * A SEED-DERIVED ground (editorial, strata) is the deployment's own brand seed
 * (`workspace.brand.colors.seed`) landed on a pinned OKLCH rung, so each fork's
 * pass carries that fork's colour instead of one platform-wide pair. Done
 * naively — `seed x alpha` per sRGB channel — the result is a function of the
 * seed's own lightness, which is arbitrary on a white-label platform: a bright
 * yellow seed at 62% black lands on a legible olive, a near-black seed at the
 * same 62% lands on something indistinguishable from black. One ratio cannot
 * serve every fork, so the composite is a PINNED LANDING instead:
 *
 *     HUE carries identity      - H_ground = H_seed, never moved.
 *     CHROMA carries intensity  - C_ground = clamp(C_seed, C_min, C_max),
 *                                 then gamut-mapped into sRGB.
 *     LIGHTNESS is the contract - L_ground = the variant's pinned rung,
 *                                 independent of the seed entirely.
 *
 * Because no seed can move L_ground, no seed can move the contrast. A seed
 * already darker than its rung is LIFTED toward it rather than darkened.
 *
 * A PINNED ground (stencil) takes the in-app card's own ground literally
 * (`ticket.palette.ts`) and ignores the seed entirely. That is not an
 * exception grudgingly made, it is what the variant IS: the stencil card is
 * one solid vivid orange for every fork, so a seed-derived pass would hand a
 * blue-seeded deployment a blue pass beside its own orange card — the two
 * surfaces of one template disagreeing about what that template looks like.
 * The literal lives in `ticket.palette.ts` and is read across, never restated
 * here, so the card and the pass cannot drift apart. It is a template
 * constant, not a deployment identity: no fork's name or brand is in it.
 *
 * INK POLARITY IS DERIVED FROM THE GROUND, NEVER DECLARED BESIDE IT. A dark
 * ground takes light ink and a vivid light ground takes near-black ink, and
 * `walletInkPolarity` reads that off the ground's own relative luminance, so a
 * variant cannot carry a polarity its ground contradicts. The alternative —
 * declaring it per variant and testing that the two agree — keeps both alive
 * and lets the next ground change break the agreement wherever the test does
 * not reach.
 *
 * Deriving it is also the only honest answer across two vendors. Apple is SENT
 * `foregroundColor`/`labelColor`; Google is sent no text colour at all and
 * derives its own from `hexBackgroundColor` on the device. A declaration would
 * promise on the Google side a value this repo never transmits, where a
 * derivation from the ground is the same computation Google itself performs.
 *
 * White on the stencil orange measures 2.91:1, under the 4.5:1 floor small
 * field text needs; the near-black tier measures 6.48:1. Dark-on-orange is the
 * card's own language too — its QR sits in a black frame — so the pass is
 * reading the template, not compromising with it.
 *
 * WHY THIS GROUND IS FREE TO BE THE BRAND. The in-app card's grounds are
 * pinned because the card draws its own QR with literal-black modules, so its
 * ground carries door-scan duty and any movement in it moves a scanner's
 * contrast. A wallet pass ground carries none — Apple and Google both draw the
 * barcode themselves, on their own white plate, beneath the fields this file
 * colours.
 *
 * THE PANEL TREATMENT. Apple's strip and Google's hero are one panel painted
 * twice, and the variant owns how: editorial and strata resolve downward
 * through a soft ramp, stencil is one solid plate at full strength — the card's
 * own flatness, which is the thing that makes it read as stencil rather than as
 * a gradient wearing its colour. Both are ink-free by construction, because
 * both vendors draw their own glyphs over the panel and own every one of them.
 *
 * THE COUNTER TONE. `counterGround` is the ground's own hue and chroma dropped
 * `COUNTER_GROUND_DROP` in OKLCH L and gamut-fitted like every other tone: one
 * colour read at two depths. It is the foot of the strip's vertical gradient,
 * whose head is the ground, so the strip resolves downward into the field rows
 * painted beneath it.
 *
 * NO SCHEDULE FORMATTER LIVES HERE. A pass states the same date string the
 * in-app card states, so both compose it through `@mhmo91/rx`'s
 * `formatDateRange` at the card's own options (`month: "long"`, the venue's
 * IANA zone, the buyer locale's BCP-47 tag). A pass-side formatter is a second
 * expression of one fact and free to disagree with the card the moment either
 * moves; the cost of having only one is that a multi-day run reads as the
 * card reads it, dates without times, which is a card decision to revisit on
 * the card rather than a pass decision to take behind it.
 *
 * Lit-free and dependency-free by construction — the functions bundle imports
 * this subpath to compose a pass without dragging the card renderer, and that
 * bundler marks every dependency outside `@momo` and `@mhmo91` external, so a colour library
 * reached for here would be missing at cold start.
 */
import type { TicketTemplateVariant } from "@momo/types";
import {
	STENCIL_LABEL_OPACITY,
	STENCIL_VALUE_OPACITY,
	TICKET_GROUNDS,
	type TicketGround,
} from "./ticket.palette.ts";

/** Where a variant's ground comes from. A closed union, because the two
 *  sources need different facts and neither shape can hold the other's: a
 *  seed-derived ground has no hex until a seed is in hand, and a pinned one
 *  has no rung to clamp anything against. */
type WalletGroundSource =
	| {
			readonly kind: "seed";
			/** OKLCH L the ground lands on regardless of the seed — the contract. */
			readonly lightness: number;
			/** OKLCH chroma floor: below it a muted seed's hue stops reading at all. */
			readonly chromaMin: number;
			/** OKLCH chroma ceiling: above it a saturated seed clips the sRGB gamut
			 *  into a flat, and the ground stops looking composited with anything. */
			readonly chromaMax: number;
	  }
	| {
			readonly kind: "pinned";
			/** The in-app card's own ground PAIR, read across whole and never
			 *  restated: its background is the pass's ground and its ink is the
			 *  colour the pass's own panel draws the card's wordmark in, so the
			 *  two surfaces of one template cannot disagree about either. */
			readonly ground: TicketGround;
	  };

export type WalletVariantDesign = {
	readonly ground: WalletGroundSource;
	/** How Apple's strip and Google's hero are produced for this variant. */
	readonly panel: WalletPanelTreatment["kind"];
};

/** Every variant's whole wallet design, keyed by variant and exhaustive by
 *  type: a fourth variant refuses to compile until it declares its ground and
 *  its panel, rather than silently inheriting a sibling's. Ink polarity is
 *  absent on purpose — `walletInkPolarity` derives it from the ground, so the
 *  two can never disagree. */
export const WALLET_VARIANT_DESIGNS: Record<
	TicketTemplateVariant,
	WalletVariantDesign
> = {
	editorial: {
		ground: { kind: "seed", lightness: 0.34, chromaMin: 0.048, chromaMax: 0.1 },
		panel: "ramp",
	},
	stencil: {
		ground: { kind: "pinned", ground: TICKET_GROUNDS.stencil },
		panel: "composition",
	},
	strata: {
		ground: { kind: "seed", lightness: 0.34, chromaMin: 0.048, chromaMax: 0.1 },
		panel: "ramp",
	},
};

/** Apple's own @1x strip size; @2x/@3x are these multiplied, which is the
 *  whole of the naming convention Wallet resolves a bundled image through. */
export const WALLET_STRIP_WIDTH = 375;
export const WALLET_STRIP_HEIGHT = 98;

/** Apple's own @1x icon size — the pass's one square mark. */
export const WALLET_ICON_SIZE = 38;

/** Google's own recommended hero-image size, per the Wallet brand guidelines'
 *  hero-image table (1032x812 px, "1032:812 (approximately 5:4)"). No pass this
 *  repo composes sends a hero — Google draws its hero UNDER the pass's data
 *  fields, a position the in-app card has no counterpart for — so these are the
 *  geometry the artwork painter is exercised at rather than a shipped surface. */
export const WALLET_HERO_WIDTH = 1032;
export const WALLET_HERO_HEIGHT = 812;

/** How far below the ground the counter tone lands, in OKLCH L. Deep enough
 *  that the strip's gradient reads as a gradient at arm's length, shallow
 *  enough that its two ends stay one colour rather than two. */
const COUNTER_GROUND_DROP = 0.16;

/**
 * How the variant's panel — Apple's strip, Google's hero — is painted. A
 * closed union rather than a bag of nullable fields, so a flat plate can never
 * carry a foot tone it has no gradient to reach.
 *
 * `ramp` falls from `top` to `bottom` down the panel's height, so it settles
 * into the field rows painted beneath it.
 *
 * `flat` is one tone edge to edge — every variant's pass icon, which Apple
 * masks into a small rounded square where any treatment reads as noise.
 *
 * `composition` is not painted at all: the band is RENDERED from the card's
 * own components, so it carries real letterforms and follows the card as the
 * card evolves. `plate` is the band's ground and `mark` the colour its
 * wordmark takes, and both are the CARD's own pair read across whole — the
 * band is the card's top region at the card's own scale, so it is the card's
 * ground under the card's ink or it is a different picture. Painting the plate
 * in a contrasting tone gives the band an edge the card does not have and
 * turns the pass's header into a section of its own; the seamlessness that
 * costs is the point, not a defect to design around.
 *
 * Neither varies ACROSS the panel, and that is load-bearing rather than
 * incidental: both vendors scale a supplied image into an allotment of their
 * own choosing and crop whatever does not fit, and both lay their own glyphs
 * over it at a position neither publishes — so a vertical edge is what a crop
 * destroys and what a long event name straddles.
 */
/** The treatments a scanline painter can produce on its own — the panel
 *  kinds made of nothing but tones. A `composition` is deliberately absent:
 *  it is rendered from the card's components, so it has no painted form and
 *  the painter's signature says so rather than carrying a dead branch. */
export type PaintedPanelTreatment = Exclude<
	WalletPanelTreatment,
	{ kind: "composition" }
>;

export type WalletPanelTreatment =
	| { readonly kind: "flat"; readonly tone: string }
	| { readonly kind: "ramp"; readonly top: string; readonly bottom: string }
	| {
			readonly kind: "composition";
			readonly plate: string;
			readonly mark: string;
	  };

/** Which tier of ink — and of brand mark — a ground takes. "light" means light
 *  artwork on a dark ground; "dark" means near-black artwork on a light one. */
export type WalletInkPolarity = "light" | "dark";

/** The relative luminance at which contrast-to-white and contrast-to-black are
 *  equal — sqrt(0.05 x 1.05) - 0.05, solving (L+0.05)/0.05 = 1.05/(L+0.05).
 *  Above it a ground is better served by dark artwork, below it by light. */
const INK_POLARITY_PIVOT = Math.sqrt(0.05 * 1.05) - 0.05;

/** Value ink and label ink, as OKLCH (L, C), per polarity — SEED-DERIVED
 *  variants only (editorial/strata). A PINNED ground (stencil) reads its own
 *  literal ink instead (walletPassPalette's own pinned branch, below) — a
 *  CONFIRMED product decision, not an oversight: brand-exact parity with the
 *  card wins over the 4.5:1 floor this ladder otherwise holds every tone to.
 *  See ticket.palette.ts's own header for the fuller record. The hue below is
 *  always the ground's own, so the two tones read as belonging to that ground
 *  rather than as neutral white or black dropped on top of it. The two-tier
 *  split is editorial/strata's own card convention: their labels run
 *  uppercase at 70% opacity (`.tkt-dim`) against full-tone values, and Apple
 *  renders `labelColor` on uppercased labels and `foregroundColor` on values,
 *  so the card's tiering maps onto the pass's two colour slots with nothing
 *  invented. The label tier is always the LOWER contrast of the two, which on
 *  a dark ground means dimmer and on a light one means lighter; both tiers
 *  clear 4.5:1 on every SEED-DERIVED ground this file can produce, which
 *  ticket-wallet.design.test.ts sweeps rather than assumes. */
const INK_TONES: Record<
	WalletInkPolarity,
	{
		readonly value: { readonly lightness: number; readonly chroma: number };
		readonly label: { readonly lightness: number; readonly chroma: number };
	}
> = {
	light: {
		value: { lightness: 0.965, chroma: 0.014 },
		label: { lightness: 0.84, chroma: 0.022 },
	},
	dark: {
		value: { lightness: 0.18, chroma: 0.02 },
		label: { lightness: 0.28, chroma: 0.03 },
	},
};

/** Below this the seed is not a hue, it is a neutral with rounding error;
 *  carrying it forward would invent brand identity the fork does not have, so
 *  an achromatic seed earns an honestly achromatic ground. */
const ACHROMATIC_CHROMA = 0.02;

export type WalletPassPalette = {
	/** `#rrggbb` — Apple `backgroundColor`, Google `hexBackgroundColor`. */
	readonly ground: string;
	/** `#rrggbb` — Apple `foregroundColor`. */
	readonly ink: string;
	/** `#rrggbb` — Apple `labelColor`. */
	readonly label: string;
	/** `#rrggbb` — the ground's own hue and chroma, `COUNTER_GROUND_DROP`
	 *  deeper in OKLCH L: the foot of the strip's vertical gradient. */
	readonly counterGround: string;
};

/** One `#rrggbb` read back as OKLCH. The seed decode and the pinned-ground
 *  decode are the same operation on different inputs, so they share it. */
function oklchOf(hex: string): {
	readonly lightness: number;
	readonly chroma: number;
	readonly hue: number;
} {
	const packed = Number.parseInt(hex.slice(1), 16);
	const red = srgbToLinear(((packed >> 16) & 0xff) / 255);
	const green = srgbToLinear(((packed >> 8) & 0xff) / 255);
	const blue = srgbToLinear((packed & 0xff) / 255);
	const long = Math.cbrt(
		0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue,
	);
	const medium = Math.cbrt(
		0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue,
	);
	const short = Math.cbrt(
		0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue,
	);
	const a = 1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short;
	const b = 0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short;
	return {
		lightness:
			0.2104542553 * long + 0.793617785 * medium - 0.0040720468 * short,
		chroma: Math.hypot(a, b),
		hue: ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360,
	};
}

/**
 * The ground itself plus the three OKLCH coordinates every other tone on the
 * pass is measured from — the one place a variant's ground is decided, read by
 * the palette and the panel alike so a tone reached through one can never
 * disagree with the same tone reached through the other.
 *
 * A pinned ground returns its own literal rather than a re-encoding of its
 * coordinates: the round trip happens to be exact today, and staying off it
 * entirely is what guarantees the pass ships the card's colour byte for byte
 * whatever a future gamut fit does.
 *
 * `chroma === 0` IS the achromatic case — every rung's floor is above zero and
 * a pinned ground carries real chroma, so only the seed gate can produce it.
 */
function groundTone(
	seedHex: string | null,
	variant: TicketTemplateVariant,
): {
	readonly hex: string;
	readonly lightness: number;
	readonly chroma: number;
	readonly hue: number;
} {
	const source = WALLET_VARIANT_DESIGNS[variant].ground;
	if (source.kind === "pinned") {
		// Lowercased only: every derived tone is emitted lowercase, and one
		// spelling across the module is what lets a consumer compare two of its
		// outputs without case-folding first. The colour is untouched.
		return {
			hex: source.ground.background.toLowerCase(),
			...oklchOf(source.ground.background),
		};
	}
	// A fork that set no seed — and a stored value that is not a `#rrggbb`
	// literal — decodes as black, whose chroma is zero, so the achromatic gate
	// below discards the hue and absent, unreadable and neutral all land on the
	// same honest grey with no branch of their own.
	const seed = oklchOf(
		seedHex !== null && /^#[0-9a-f]{6}$/i.test(seedHex) ? seedHex : "#000000",
	);
	const achromatic = seed.chroma < ACHROMATIC_CHROMA;
	const hue = achromatic ? 0 : seed.hue;
	const chroma = achromatic
		? 0
		: Math.min(source.chromaMax, Math.max(source.chromaMin, seed.chroma));
	return {
		hex: gamutHex(source.lightness, chroma, hue),
		lightness: source.lightness,
		chroma,
		hue,
	};
}

/** The OKLCH lightness this variant's ground actually landed on, whichever way
 *  it was decided — what a fork's own move onto the rung is measured against. */
export function walletGroundLightness(
	seedHex: string | null,
	variant: TicketTemplateVariant,
): number {
	return groundTone(seedHex, variant).lightness;
}

/**
 * Which tier of BRAND MARK this variant's ground takes — read off the
 * ground's own relative luminance, which is both the measure the 4.5:1
 * contract is stated in and the computation Google performs on the device.
 * Physics, not brand preference: a mark is artwork laid on the actual ground
 * colour, so a ground bright enough to need dark artwork takes the dark mark
 * unconditionally, whatever ink `walletPassPalette` decides to emit for that
 * SAME ground — see that function's own pinned branch, which reads
 * `TICKET_GROUNDS.stencil.ink` directly rather than through this function,
 * for exactly that reason: a pinned ground's INK is a confirmed brand
 * decision (ticket.palette.ts's own header) that may knowingly miss the
 * 4.5:1 floor, but its MARK is not that decision's to move — the mark a
 * deployment derives for a dark-artwork ground is the one asset a bright
 * ground must never be sent, measured, not assumed
 * (walletGoogle.api.test.ts's own "brand mark's polarity follows the
 * ground it lands on" block is what enforces this at the mark-resolution
 * seam, and is what this function must keep agreeing with regardless of
 * what `walletPassPalette` does for a pinned ground's ink).
 */
export function walletInkPolarity(
	seedHex: string | null,
	variant: TicketTemplateVariant,
): WalletInkPolarity {
	return relativeLuminance(groundTone(seedHex, variant).hex) >
		INK_POLARITY_PIVOT
		? "dark"
		: "light";
}

export function walletPassPalette(
	seedHex: string | null,
	variant: TicketTemplateVariant,
): WalletPassPalette {
	const source = WALLET_VARIANT_DESIGNS[variant].ground;
	if (source.kind === "pinned") {
		const { hex, lightness, chroma, hue } = groundTone(seedHex, variant);
		// The card's own literal ink, pre-blended against the card's own
		// opacity convention for each tier (STENCIL_*_OPACITY,
		// ticket.palette.ts) — CONFIRMED brand-exact parity with the card,
		// not the WCAG-optimal INK_TONES tier below.
		const ink = source.ground.ink.toLowerCase();
		return {
			ground: hex,
			ink: alphaComposite(ink, STENCIL_VALUE_OPACITY, hex),
			label: alphaComposite(ink, STENCIL_LABEL_OPACITY, hex),
			counterGround: gamutHex(lightness - COUNTER_GROUND_DROP, chroma, hue),
		};
	}
	const { hex, lightness, chroma, hue } = groundTone(seedHex, variant);
	const tones = INK_TONES[walletInkPolarity(seedHex, variant)];
	const achromatic = chroma === 0;
	return {
		ground: hex,
		ink: gamutHex(
			tones.value.lightness,
			achromatic ? 0 : tones.value.chroma,
			hue,
		),
		label: gamutHex(
			tones.label.lightness,
			achromatic ? 0 : tones.label.chroma,
			hue,
		),
		counterGround: gamutHex(lightness - COUNTER_GROUND_DROP, chroma, hue),
	};
}

/**
 * How this ticket's pass panel is painted — Apple's bundled strip and
 * Google's uploaded hero both, so the two vendors render one design rather
 * than two that happen to share a background colour.
 */
export function walletPanelTreatment(
	seedHex: string | null,
	variant: TicketTemplateVariant,
): WalletPanelTreatment {
	const design = WALLET_VARIANT_DESIGNS[variant];
	if (design.panel === "composition") {
		// The card's own ground pair, read across whole: the band IS the card's
		// top region, so its ground is the card's ground and its wordmark takes
		// the card's ink. A seed-derived variant has no card ground to read, so
		// it falls back to the pass's own ground with its value ink.
		const palette = walletPassPalette(seedHex, variant);
		return design.ground.kind === "pinned"
			? {
					kind: "composition",
					plate: design.ground.ground.background.toLowerCase(),
					mark: design.ground.ground.ink.toLowerCase(),
				}
			: { kind: "composition", plate: palette.ground, mark: palette.ink };
	}
	const { hex, lightness, chroma, hue } = groundTone(seedHex, variant);
	return design.panel === "flat"
		? { kind: "flat", tone: hex }
		: {
				kind: "ramp",
				top: hex,
				bottom: gamutHex(lightness - COUNTER_GROUND_DROP, chroma, hue),
			};
}

/**
 * Apple pass colors as `rgb(r, g, b)` triples — the only documented format.
 * Value ink and label ink are distinct tones of the seed's own hue, which is
 * the two-tier split Apple's `foregroundColor`/`labelColor` pair asks for.
 */
export function appleWalletColors(
	variant: TicketTemplateVariant,
	seedHex: string | null,
): { backgroundColor: string; foregroundColor: string; labelColor: string } {
	const palette = walletPassPalette(seedHex, variant);
	return {
		backgroundColor: rgbTriple(palette.ground),
		foregroundColor: rgbTriple(palette.ink),
		labelColor: rgbTriple(palette.label),
	};
}

/** Google pass background as the derived ground's own `#rrggbb` hex. */
export function googleWalletHexBackground(
	variant: TicketTemplateVariant,
	seedHex: string | null,
): string {
	return walletPassPalette(seedHex, variant).ground;
}

function rgbTriple(hex: string): string {
	const value = Number.parseInt(hex.slice(1), 16);
	return `rgb(${(value >> 16) & 0xff}, ${(value >> 8) & 0xff}, ${value & 0xff})`;
}

/** `foreground` at `alpha` over `background`, both `#rrggbb` — the flat RGB
 *  a `color: foreground; opacity: alpha` CSS rule paints once flattened onto
 *  its own solid ground. Apple's `foregroundColor`/`labelColor` carry no
 *  alpha channel, so a pinned ground's card-CSS opacity convention has to be
 *  pre-blended into a flat colour here rather than sent as-is. */
function alphaComposite(
	foreground: string,
	alpha: number,
	background: string,
): string {
	const fg = Number.parseInt(foreground.slice(1), 16);
	const bg = Number.parseInt(background.slice(1), 16);
	const mix = (shift: number): string => {
		const blended =
			((fg >> shift) & 0xff) * alpha + ((bg >> shift) & 0xff) * (1 - alpha);
		return Math.round(Math.min(255, Math.max(0, blended)))
			.toString(16)
			.padStart(2, "0");
	};
	return `#${mix(16)}${mix(8)}${mix(0)}`;
}

/** The requested chroma reduced until the colour is representable in sRGB, hue
 *  and lightness held — the standard CSS Color 4 gamut-map direction, and the
 *  reason a fully saturated seed lands on a rich ground rather than a clipped
 *  flat. */
function gamutHex(lightness: number, chroma: number, hue: number): string {
	let low = 0;
	let high = chroma;
	if (!representable(oklchToSrgb(lightness, chroma, hue))) {
		for (let step = 0; step < 24; step += 1) {
			const middle = (low + high) / 2;
			if (representable(oklchToSrgb(lightness, middle, hue))) low = middle;
			else high = middle;
		}
	} else {
		low = chroma;
	}
	const [red, green, blue] = oklchToSrgb(lightness, low, hue);
	return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

function oklchToSrgb(
	lightness: number,
	chroma: number,
	hue: number,
): readonly [number, number, number] {
	const a = chroma * Math.cos((hue * Math.PI) / 180);
	const b = chroma * Math.sin((hue * Math.PI) / 180);
	const long = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
	const medium = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
	const short = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
	return [
		linearToSrgb(
			4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short,
		),
		linearToSrgb(
			-1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short,
		),
		linearToSrgb(
			-0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short,
		),
	];
}

function representable(rgb: readonly [number, number, number]): boolean {
	return rgb.every((value) => value >= -0.0005 && value <= 1.0005);
}

function channel(value: number): string {
	return Math.round(Math.min(1, Math.max(0, value)) * 255)
		.toString(16)
		.padStart(2, "0");
}

function srgbToLinear(value: number): number {
	return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(value: number): number {
	return value <= 0.0031308
		? 12.92 * value
		: 1.055 * value ** (1 / 2.4) - 0.055;
}

/** WCAG 2.x relative luminance — the measure the ink contract is stated in,
 *  deliberately not OKLCH L: the accessibility floor a pass's legibility is
 *  judged against is defined in this space, so the check runs in it too. */
function relativeLuminance(hex: string): number {
	const packed = Number.parseInt(hex.slice(1), 16);
	return (
		0.2126 * srgbToLinear(((packed >> 16) & 0xff) / 255) +
		0.7152 * srgbToLinear(((packed >> 8) & 0xff) / 255) +
		0.0722 * srgbToLinear((packed & 0xff) / 255)
	);
}

export function contrastRatio(first: string, second: string): number {
	const a = relativeLuminance(first);
	const b = relativeLuminance(second);
	return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
